import { Injectable, DestroyRef, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { HttpErrorResponse } from "@angular/common/http";
import { Router } from "@angular/router";
import Swal, { SweetAlertResult } from "sweetalert2";
import { DataService } from "../data.service";
import { TenantService } from "../tenant.service";
import { LoggerService } from "../logger.service";
import { VCloudSmartApiService } from "../v-cloud-smart-api.service";
import { EventsService } from "../events.service";
import { DownloadType } from "../../constant/common-table";
import { REGEX_COMMA, REGEX_QUOTE } from "../../constant/validation";
import { Currency } from "../../models/currency";
import { GroupByPayload } from "../../models/vcloud-api";
import { TableColumn, TableRow } from "../../models/common-table";

export interface CommonTableDownloadContext {
  tableName: string;
  downloadFileNamePrefix: string;
  type: DownloadType;
  columns: TableColumn[];
  rows: TableRow[];
  tableRows: TableRow[];
  selectableRows: boolean;
  downloadKeys: string[];
  downloadSerializer: (row: TableRow, keys: string[]) => string | null;
}

@Injectable({
  providedIn: "root",
})
export class CommonTableDownloadService {
  private dataService: DataService = inject(DataService);
  private tenant: TenantService = inject(TenantService);
  private log: LoggerService = inject(LoggerService);
  private vcs: VCloudSmartApiService = inject(VCloudSmartApiService);
  private events: EventsService = inject(EventsService);
  private router: Router = inject(Router);

  public shouldUseAsyncDownload(
    tableName: string,
    type: DownloadType,
    asyncExportEnabled: boolean,
    hasAsyncRequestBody: boolean,
  ): boolean {
    if (tableName.startsWith("llm-table-")) {
      return type === "all" && asyncExportEnabled;
    }

    return asyncExportEnabled && hasAsyncRequestBody && type === "all";
  }

  public startAsyncDownload(
    requestBody: GroupByPayload,
    currency: Currency,
    columns: TableColumn[],
    destroyRef: DestroyRef,
  ): void {
    this.vcs
      .groupByAsync(requestBody, currency, columns)
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe({
        next: () => {
          Swal.fire({
            title: "<strong>Export Initiated</strong>",
            icon: "success",
            html: `<strong>NOTE:</strong> On clicking <b>Go To Export Page</b>, you will be redirected to the Data Export page where you can download the report.<br><br>
            The export time depends on the amount of time you have access to, the filters applied, and the report you are on.<br><br>`,
            showCloseButton: true,
            showCancelButton: true,
            focusConfirm: false,
            confirmButtonText: '<i class="fa fa-download"></i> Go To Export Page',
            cancelButtonText: '<i class="fa fa-check"></i> Close',
          }).then((result: SweetAlertResult<Awaited<unknown>>) => {
            if (result.isConfirmed) {
              const match: RegExpMatchArray | null = window.location.href.match(/\/(aws|gcp|azure|drcc|oci)\//);
              const redirectUrl: string = match ? `/dashboard#vcloudsmart/${match[1]}` : `/dashboard#vcloudsmart`;
              this.router.navigateByUrl(redirectUrl);
            }
          });
        },
        error: (error: Error | HttpErrorResponse) => {
          this.events.displayErrorToUser(error, "Export data from table");
        },
      });
  }

  public async downloadSync(context: CommonTableDownloadContext): Promise<void> {
    const {
      type,
      columns,
      selectableRows,
      downloadKeys,
      rows,
      tableRows,
      downloadSerializer,
      tableName,
      downloadFileNamePrefix,
    } = context;

    let exportColumns: TableColumn[] = [...columns];

    this.log.info({
      msg: "initiating synchronous download",
      type,
      columns: exportColumns,
      selectableRows,
    });

    if (selectableRows) {
      exportColumns = exportColumns.slice(1);
    }

    const deselectedColumns: TableColumn[] = exportColumns.filter((column: TableColumn) => !column.selected);
    const columnSelectionUsed: boolean = (deselectedColumns?.length ?? 0) > 0;

    this.log.info({ columnSelectionUsed, deselectedColumns, columns: exportColumns });

    let onlyDownloadVisibleColumns = false;

    if (columnSelectionUsed) {
      const userConsent: SweetAlertResult<Awaited<unknown>> = await Swal.fire({
        title: "Only Visible Columns?",
        text: "Do you want to download only visible columns?",
        confirmButtonColor: this.tenant.colors.primary,
        cancelButtonColor: this.tenant.colors.secondary,
        showConfirmButton: true,
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        backdrop: false,
      });

      onlyDownloadVisibleColumns = userConsent?.isConfirmed ?? false;
      if (onlyDownloadVisibleColumns) {
        exportColumns = exportColumns.filter((column: TableColumn) => column.selected);
      }
    }

    let rowKeys: string[];
    let headers: string[];

    if ((downloadKeys?.length ?? 0) === 0) {
      rowKeys = exportColumns.map((column: TableColumn) => column.title);
      headers = exportColumns.map((column: TableColumn) => column.label as string);
    } else {
      rowKeys = downloadKeys;
      headers = downloadKeys;
    }

    const headings: string = headers
      .map((header: string) => header.replace(REGEX_QUOTE, "").replace(REGEX_COMMA, ""))
      .join(",");

    let rowsToSave: TableRow[];

    if (type === "filtered") {
      rowsToSave = tableRows;
    } else if (type === "selected") {
      rowsToSave = rows.filter((row: TableRow) => row?.[0]?.value === true);
    } else {
      rowsToSave = rows;
    }

    if (onlyDownloadVisibleColumns) {
      rowsToSave = rowsToSave.map((row: TableRow) =>
        row.filter((_, index: number) => columns?.[index]?.selected === true),
      );
    }

    if (selectableRows) {
      rowsToSave = rowsToSave.map((row: TableRow) => row.slice(1));
    }

    this.log.info(`Saving ${type} rows (count: ${rowsToSave.length}/${rows.length})`);

    const csvRows: string[] = [headings];

    for (const row of rowsToSave) {
      const serializedRow: string | null = downloadSerializer(row, rowKeys);
      if (serializedRow !== null) {
        csvRows.push(serializedRow);
      }
    }

    this.dataService.download(csvRows.join("\n"), this.getDownloadFileName(tableName, downloadFileNamePrefix));
  }

  private getDownloadFileName(tableName: string, downloadFileNamePrefix: string): string {
    const prefix: string = downloadFileNamePrefix !== "" ? downloadFileNamePrefix : tableName;
    return `${prefix}-${new Date().toISOString()}.csv`;
  }
}
